import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, X } from "lucide-react";
import { listChecks } from "@/api/checks";

export function TagPicker({
  value,
  onChange,
}: {
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const checks = useQuery({
    queryKey: ["checks"],
    queryFn: listChecks,
    staleTime: 60_000,
  });

  // Vocabulary = every check name (enabled or not) so previously-tagged
  // domains from a since-disabled check stay filterable. Anything in the
  // current selection but missing from the vocab (e.g. a removed check)
  // is appended so the user can still un-select it.
  const options = useMemo(() => {
    const names = new Set((checks.data ?? []).map((c) => c.name));
    for (const v of value) names.add(v);
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [checks.data, value]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const toggle = (name: string) => {
    if (value.includes(name)) onChange(value.filter((v) => v !== name));
    else onChange([...value, name]);
  };

  const label =
    value.length === 0
      ? "any tag"
      : value.length === 1
        ? value[0]
        : `${value.length} tags`;

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        className="input w-56 flex items-center justify-between gap-2 text-left"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={value.length === 0 ? "text-muted" : ""}>{label}</span>
        <ChevronDown className="h-3.5 w-3.5 text-muted shrink-0" />
      </button>

      {open ? (
        <div
          className="absolute z-20 mt-1 w-64 max-h-72 overflow-auto rounded-md border border-border bg-card shadow-lg p-1"
          role="listbox"
        >
          {value.length > 0 ? (
            <button
              type="button"
              className="w-full text-left text-xs text-muted hover:text-fg px-2 py-1.5 flex items-center gap-1"
              onClick={() => onChange([])}
            >
              <X className="h-3 w-3" /> Clear selection
            </button>
          ) : null}
          {options.length === 0 ? (
            <div className="px-2 py-1.5 text-xs text-muted">No tags yet</div>
          ) : (
            options.map((name) => {
              const checked = value.includes(name);
              return (
                <label
                  key={name}
                  className="flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-subtle cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(name)}
                    className="cursor-pointer"
                  />
                  <span className="truncate">{name}</span>
                </label>
              );
            })
          )}
        </div>
      ) : null}
    </div>
  );
}
